/**
 * IntakeFormFields — TASK-31 (client booking side)
 *
 * Renders dynamic intake questions on the BookingPage details step.
 * Validates required fields and passes answers back to parent.
 */

import { useState } from 'react';
import { useI18n } from '@/shared/i18n/useT';
import type { IntakeQuestion } from '@/shared/calendar/intakeTypes';

export type { IntakeQuestion };

export interface IntakeAnswer {
  questionId: string;
  questionLabel: string;
  type: IntakeQuestion['type'];
  value: string | string[] | { url: string; name: string; sizeBytes?: number };
}

export type IntakeAnswersMap = Record<string, IntakeAnswer>;

interface Props {
  readonly questions: IntakeQuestion[];
  readonly answers: IntakeAnswersMap;
  readonly onChange: (answers: IntakeAnswersMap) => void;
  readonly showErrors?: boolean;
}

export function IntakeFormFields({ questions, answers, onChange, showErrors }: Props) {
  const { t } = useI18n();
  const [uploadingId, setUploadingId] = useState<string | null>(null);

  const set = (q: IntakeQuestion, value: IntakeAnswer['value']) => {
    onChange({
      ...answers,
      [q.id]: { questionId: q.id, questionLabel: q.label, type: q.type, value },
    });
  };

  const getStr = (id: string): string => {
    const a = answers[id];
    return typeof a?.value === 'string' ? a.value : '';
  };

  const getArr = (id: string): string[] => {
    const a = answers[id];
    return Array.isArray(a?.value) ? (a.value as string[]) : [];
  };

  const isMissing = (q: IntakeQuestion) => {
    if (!q.required) return false;
    const a = answers[q.id];
    if (!a) return true;
    if (typeof a.value === 'string') return a.value.trim().length === 0;
    if (Array.isArray(a.value)) return a.value.length === 0;
    if (typeof a.value === 'object' && 'url' in a.value) return !a.value.url;
    return true;
  };

  if (questions.length === 0) return null;

  return (
    <fieldset className="space-y-4">
      <legend className="text-sm font-medium text-fg">{t('calendarIntake.title')}</legend>
      {questions.map((q) => {
        const error = showErrors && isMissing(q);
        return (
          <div key={q.id} className="space-y-1">
            <label className="text-xs text-muted">
              {q.label}
              {q.required && <span className="ml-1 text-error" aria-label="required">*</span>}
            </label>

            {(q.type === 'text' || q.type === 'phone') && (
              <input
                type={q.type === 'phone' ? 'tel' : 'text'}
                placeholder={q.placeholder}
                value={getStr(q.id)}
                onChange={(e) => set(q, e.target.value)}
                aria-required={q.required}
                aria-invalid={error}
                className="w-full rounded-full border border-border-strong bg-surface px-3 py-2 text-sm text-fg data-[error=true]:border-error"
                data-error={error}
              />
            )}

            {q.type === 'textarea' && (
              <textarea
                placeholder={q.placeholder}
                value={getStr(q.id)}
                onChange={(e) => set(q, e.target.value)}
                aria-required={q.required}
                aria-invalid={error}
                rows={3}
                className="w-full rounded-token border border-border-strong bg-surface px-3 py-2 text-sm text-fg data-[error=true]:border-error"
                data-error={error}
                maxLength={2000}
              />
            )}

            {q.type === 'dropdown' && (
              <select
                value={getStr(q.id)}
                onChange={(e) => set(q, e.target.value)}
                aria-required={q.required}
                aria-invalid={error}
                className="w-full rounded-full border border-border-strong bg-surface px-3 py-2 text-sm text-fg"
              >
                <option value="">{q.placeholder ?? t('calendarIntake.optional')}</option>
                {(q.options ?? []).map((opt: string) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            )}

            {q.type === 'checkboxes' && (
              <div className="space-y-1 rounded-token border border-border bg-surface-2 p-2">
                {(q.options ?? []).map((opt: string) => {
                  const checked = getArr(q.id).includes(opt);
                  return (
                    <label key={opt} className="flex items-center gap-2 text-sm text-fg">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          const cur = getArr(q.id);
                          set(q, e.target.checked ? [...cur, opt] : cur.filter((v) => v !== opt));
                        }}
                        className="rounded"
                      />
                      {opt}
                    </label>
                  );
                })}
              </div>
            )}

            {q.type === 'file' && (
              <div>
                <input
                  type="file"
                  disabled={uploadingId === q.id}
                  accept={(q.allowedFileTypes ?? ['.pdf', '.png', '.jpg', '.docx']).join(',')}
                  aria-required={q.required}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const maxBytes = (q.maxFileSizeMb ?? 10) * 1024 * 1024;
                    if (file.size > maxBytes) {
                      alert(t('calendarIntake.validationError.file_too_large'));
                      return;
                    }
                    // For now, use a data URL as placeholder
                    // In production this would upload to Supabase Storage
                    setUploadingId(q.id);
                    try {
                      const reader = new FileReader();
                      await new Promise<void>((res) => {
                        reader.onloadend = () => {
                          set(q, { url: reader.result as string, name: file.name, sizeBytes: file.size });
                          res();
                        };
                        reader.readAsDataURL(file);
                      });
                    } finally {
                      setUploadingId(null);
                    }
                  }}
                  className="w-full text-sm text-fg file:mr-3 file:rounded-full file:border-0 file:bg-accent file:px-3 file:py-1 file:text-xs file:text-accent-fg"
                />
              </div>
            )}

            {error && (
              <p className="text-xs text-error" role="alert">
                {t('calendarIntake.validationError.required')}
              </p>
            )}
          </div>
        );
      })}
    </fieldset>
  );
}
